<script lang="ts">
export default {
  name: 'UserHome',
};
</script>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref } from 'vue';
import { Grip, SlidersHorizontal, Trash2 } from '@lucide/vue';
import DashboardWidgetService from '@/services/v2/DashboardWidgetService';
import DashboardTemplateService from '@/services/v2/DashboardTemplateService';
import widgetRegistry from '@/components/dashboard/widgetRegistry';
import DashboardWidget from '@/components/dashboard/DashboardWidget.vue';
import AddWidgetModal from '@/components/dashboard/AddWidgetModal.vue';
import UniversalSearch from '@/components/UniversalSearch.vue';
import BrowseTemplatesModal from '@/components/dashboard/BrowseTemplatesModal.vue';
import CreateTemplateModal from '@/components/dashboard/CreateTemplateModal.vue';
import ConfirmModal from '@/components/v2/ConfirmModal.vue';
import AppButton from '@/components/v2/AppButton.vue';
import { IsUserAnAdmin } from '@/utilities/AuthUtils';
import Store, { Commit } from '@/store';
import { StoreMutationType } from '@/types/StoreMutationType';
import { userProfileService } from '@/services/api';
import type { DashboardColumn, DashboardLayout, DashboardWidget as DashboardWidgetType } from '@/types/v2/DashboardWidget';

const layout = ref<DashboardLayout>({ columns: [] });
const editMode = ref(false);
const showAddModal = ref(false);
const loading = ref(true);
const pendingConfigs = ref<Map<number, Record<string, unknown>>>(new Map());
const selectedColumnId = ref<number | null>(null);
const showBrowseTemplates = ref(false);
const templatesAutoOpened = ref(false);
const showCreateTemplate = ref(false);
const confirmModalRef = ref<InstanceType<typeof ConfirmModal> | null>(null);
const isAdmin = computed(() => IsUserAnAdmin(Store.state.user));
const isCurrentHomepage = computed(() => Store.state.user?.user?.homepagePath === '/user/home');
const settingHomepage = ref(false);

async function setAsHomepage() {
  settingHomepage.value = true;
  try {
    await userProfileService.updateHomepagePreference({ homepagePath: '/user/home' });
    if (Store.state.user?.user) {
      Store.state.user.user.homepagePath = '/user/home';
    }
    Commit(StoreMutationType.SET_FLASH_MESSAGE, { text: 'Homepage updated', color: 'success' });
  } catch {
    Commit(StoreMutationType.SET_FLASH_MESSAGE, { text: 'Failed to update homepage', color: 'error' });
  } finally {
    settingHomepage.value = false;
  }
}

// Drag state
const dragWidgetId = ref<number | null>(null);
const dragFromColumnId = ref<number | null>(null);
const dragColumnId = ref<number | null>(null);
const draggingOverAddColumn = ref(false);
/** Shallow copy of each column's widgets at widget drag start — used to restore on drag cancel */
const widgetOrderSnapshot = ref<DashboardWidgetType[][] | null>(null);
/** Shallow copy of column order at column drag start — used to restore on drag cancel */
const columnOrderSnapshot = ref<DashboardColumn[] | null>(null);

const columnSpanClassMap: Record<number, string> = {
  1: 'md:col-span-1 lg:col-span-2',
  2: 'md:col-span-2 lg:col-span-4',
  3: 'md:col-span-2 lg:col-span-6',
  4: 'md:col-span-2 lg:col-span-8',
};

const internalColumnGridClassMap: Record<number, string> = {
  1: 'md:grid-cols-1',
  2: 'md:grid-cols-2',
  3: 'md:grid-cols-3',
  4: 'md:grid-cols-4',
};

const lgColumnStartClassMap: Record<number, string> = {
  1: '',
  2: 'lg:col-start-2',
  3: 'lg:col-start-3',
  4: 'lg:col-start-4',
  5: 'lg:col-start-5',
  6: 'lg:col-start-6',
  7: 'lg:col-start-7',
  8: 'lg:col-start-8',
};

const columns = computed(() => layout.value.columns);
const totalWidgets = computed(() => columns.value.reduce((sum, column) => sum + column.widgets.length, 0));
const canAddColumn = computed(() => true);
const lgRowStartsByIndex = computed(() => {
  const starts = new Map<number, number>();
  const maxUnits = 8;

  let rowFirstIndex = 0;
  let rowUsed = 0;

  for (let i = 0; i < columns.value.length; i += 1) {
    const logicalSpan = Math.max(1, Math.min(columns.value[i].colSpan, 4));
    const span = logicalSpan * 2;
    if (rowUsed + span > maxUnits) {
      const start = Math.floor((maxUnits - rowUsed) / 2) + 1;
      starts.set(rowFirstIndex, start);
      rowFirstIndex = i;
      rowUsed = span;
      continue;
    }
    rowUsed += span;
  }

  if (columns.value.length > 0) {
    const start = Math.floor((maxUnits - rowUsed) / 2) + 1;
    starts.set(rowFirstIndex, start);
  }

  return starts;
});

/**
 * Whether the "Add column" slot fits on the same lg row as the last column (8-unit grid; 1 unit for the slot).
 * Mirrors row packing in `lgRowStartsByIndex`. When false, the slot wraps and should span full width + center.
 */
const addColumnFitsOnLastRowLg = computed(() => {
  if (columns.value.length === 0) return true;
  const maxUnits = 8;
  let rowUsed = 0;
  for (let i = 0; i < columns.value.length; i += 1) {
    const logicalSpan = Math.max(1, Math.min(columns.value[i].colSpan, 4));
    const span = logicalSpan * 2;
    if (rowUsed + span > maxUnits) {
      rowUsed = span;
    } else {
      rowUsed += span;
    }
  }
  return rowUsed + 1 <= maxUnits;
});

const addColumnSlotClass = computed(() =>
  addColumnFitsOnLastRowLg.value
    ? 'col-span-1 flex w-full items-center justify-center self-stretch py-4 lg:w-auto lg:min-w-18 lg:justify-center lg:py-0'
    : 'col-span-full flex w-full items-center justify-center self-stretch py-4 lg:py-0',
);

const widgetWidthSyncMetaKey = '__layout';
const widgetWidthSyncPropKey = 'widthSynced';
const activeColumnSliderId = ref<number | null>(null);
const activeColumnSliderHostRef = ref<HTMLElement | null>(null);
const activeColumnSliderPanelRef = ref<HTMLElement | null>(null);
const activeColumnSliderStyle = ref<Record<string, string>>({});

async function loadLayout() {
  loading.value = true;
  try {
    layout.value = await DashboardWidgetService.getLayout();
    if (layout.value.columns.length > 0) {
      if (!selectedColumnId.value || !layout.value.columns.some((column) => column.id === selectedColumnId.value)) {
        selectedColumnId.value = layout.value.columns[0].id;
      }
    } else {
      selectedColumnId.value = null;
    }
  } catch {
    // silently fail — empty dashboard
  } finally {
    loading.value = false;
  }
}

function openAddWidgetModal(columnId: number) {
  selectedColumnId.value = columnId;
  showAddModal.value = true;
}

function enterEditModeAndAddFirstWidget() {
  editMode.value = true;
  const first = columns.value[0];
  if (first) openAddWidgetModal(first.id);
}

async function addWidget(widgetType: string) {
  const definition = widgetRegistry[widgetType];
  if (!definition) return;

  const targetColumnId = selectedColumnId.value ?? layout.value.columns[0]?.id;
  if (!targetColumnId) return;

  const column = layout.value.columns.find((c) => c.id === targetColumnId);
  const desiredSpan = Math.max(1, Math.min(definition.defaultColSpan, 4));
  const initialColSpan = column ? Math.max(1, Math.min(Math.max(column.colSpan, desiredSpan), 4)) : desiredSpan;

  // Expand the column if the widget's default span is wider
  if (column && desiredSpan > column.colSpan) {
    await updateColumnSpan(column, desiredSpan);
  }

  const baseConfig: Record<string, unknown> = definition.defaultConfig && isRecord(definition.defaultConfig) ? { ...definition.defaultConfig } : {};
  const configWithSync = withWidgetWidthSync(baseConfig, true);

  try {
    await DashboardWidgetService.create({
      widgetType,
      config: JSON.stringify(configWithSync),
      colSpan: initialColSpan,
      columnId: targetColumnId,
    });
    await loadLayout();
    showAddModal.value = false;
  } catch {
    // handle error silently
  }
}

async function removeWidget(id: number) {
  try {
    await DashboardWidgetService.remove(id);
    await loadLayout();
  } catch {
    // handle error silently
  }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function getResolvedWidgetConfig(widget: DashboardWidgetType): Record<string, unknown> {
  if (widget.config) {
    try {
      const parsed = JSON.parse(widget.config) as unknown;
      if (isRecord(parsed)) return { ...parsed };
    } catch {
      // Fall back to default config.
    }
  }
  const defaults = widgetRegistry[widget.widgetType]?.defaultConfig;
  if (defaults && isRecord(defaults)) return { ...defaults };
  return {};
}

function getEffectiveWidgetConfig(widget: DashboardWidgetType): Record<string, unknown> {
  const pending = pendingConfigs.value.get(widget.id);
  if (pending) return { ...pending };
  return getResolvedWidgetConfig(widget);
}

function withWidgetWidthSync(config: Record<string, unknown>, widthSynced: boolean): Record<string, unknown> {
  const meta = isRecord(config[widgetWidthSyncMetaKey]) ? config[widgetWidthSyncMetaKey] : {};
  return {
    ...config,
    [widgetWidthSyncMetaKey]: {
      ...meta,
      [widgetWidthSyncPropKey]: widthSynced,
    },
  };
}

function isWidgetWidthSynced(widget: DashboardWidgetType): boolean {
  const config = getResolvedWidgetConfig(widget);
  const meta = config[widgetWidthSyncMetaKey];
  if (!isRecord(meta)) return true;
  const flag = meta[widgetWidthSyncPropKey];
  return typeof flag === 'boolean' ? flag : true;
}

function buildWidgetConfigWithWidthSync(widget: DashboardWidgetType, widthSynced: boolean): string {
  const config = withWidgetWidthSync(getEffectiveWidgetConfig(widget), widthSynced);
  return JSON.stringify(config);
}

function takeWidgetOrderSnapshot() {
  widgetOrderSnapshot.value = columns.value.map((c) => [...c.widgets]);
}

function restoreWidgetOrderSnapshot() {
  if (!widgetOrderSnapshot.value) return;
  columns.value.forEach((col, i) => {
    const snap = widgetOrderSnapshot.value![i];
    if (!snap) return;
    col.widgets.splice(0, col.widgets.length, ...snap);
  });
}

function clearWidgetDragState() {
  dragWidgetId.value = null;
  dragFromColumnId.value = null;
  widgetOrderSnapshot.value = null;
  draggingOverAddColumn.value = false;
}

function onDragStart(e: DragEvent, widgetId: number, columnId: number) {
  dragWidgetId.value = widgetId;
  dragFromColumnId.value = columnId;
  takeWidgetOrderSnapshot();
  if (e.dataTransfer) {
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', String(widgetId));
  }
}

function takeColumnOrderSnapshot() {
  columnOrderSnapshot.value = [...columns.value];
}

function restoreColumnOrderSnapshot() {
  if (!columnOrderSnapshot.value) return;
  layout.value.columns.splice(0, layout.value.columns.length, ...columnOrderSnapshot.value);
  layout.value.columns.forEach((col, idx) => {
    col.position = idx;
  });
}

function clearColumnDragState() {
  dragColumnId.value = null;
  columnOrderSnapshot.value = null;
}

function onColumnDragStart(e: DragEvent, columnId: number) {
  dragColumnId.value = columnId;
  takeColumnOrderSnapshot();
  if (e.dataTransfer) {
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', String(columnId));
  }
}

/**
 * Container-level dragover for columns: compute target index from mouse position
 * against column child midpoints. Uses clientX primarily but also accounts for
 * wrapped rows via clientY.
 */
function onColumnContainerDragOver(e: DragEvent) {
  if (dragColumnId.value === null) return;
  e.preventDefault();

  const container = e.currentTarget as HTMLElement;
  const mouseX = e.clientX;
  const mouseY = e.clientY;
  const children = Array.from(container.children).slice(0, columns.value.length) as HTMLElement[];

  let targetIndex = children.length;
  for (let i = 0; i < children.length; i++) {
    const rect = children[i].getBoundingClientRect();
    if (mouseY < rect.top) {
      targetIndex = i;
      break;
    }
    if (mouseY < rect.bottom && mouseX < rect.left + rect.width / 2) {
      targetIndex = i;
      break;
    }
  }

  const currentIndex = columns.value.findIndex((c) => c.id === dragColumnId.value);
  if (currentIndex < 0) return;
  if (targetIndex === currentIndex || targetIndex === currentIndex + 1) return;

  const [col] = columns.value.splice(currentIndex, 1);
  const insertAt = targetIndex > currentIndex ? targetIndex - 1 : targetIndex;
  columns.value.splice(Math.max(0, Math.min(insertAt, columns.value.length)), 0, col);
  columns.value.forEach((c, idx) => {
    c.position = idx;
  });
}

async function commitColumnDrop() {
  if (dragColumnId.value === null) return;

  if (columnOrderSnapshot.value) {
    const changed = columns.value.some((col, i) => col.id !== columnOrderSnapshot.value![i]?.id);
    if (!changed) {
      clearColumnDragState();
      return;
    }
  }

  clearColumnDragState();

  try {
    await DashboardWidgetService.reorderColumns(columns.value.map((column) => column.id));
  } catch {
    await loadLayout();
  }
}

function onColumnDragEnd() {
  if (dragColumnId.value !== null) {
    restoreColumnOrderSnapshot();
  }
  clearColumnDragState();
}

/**
 * Container-level dragover: compute target index from mouse Y against child midpoints.
 * Works directly on the live array so the DOM stays stable after each move (no oscillation).
 */
function onWidgetContainerDragOver(e: DragEvent, columnId: number) {
  if (dragWidgetId.value === null) return;
  e.preventDefault();

  const col = columns.value.find((c) => c.id === columnId);
  if (!col) return;

  const container = e.currentTarget as HTMLElement;
  const mouseY = e.clientY;
  const children = Array.from(container.children) as HTMLElement[];

  let targetIndex = children.length;
  for (let i = 0; i < children.length; i++) {
    const rect = children[i].getBoundingClientRect();
    if (mouseY < rect.top + rect.height / 2) {
      targetIndex = i;
      break;
    }
  }

  const currentIndex = col.widgets.findIndex((w) => w.id === dragWidgetId.value);

  if (currentIndex >= 0) {
    if (targetIndex === currentIndex || targetIndex === currentIndex + 1) return;
    const [widget] = col.widgets.splice(currentIndex, 1);
    const insertAt = targetIndex > currentIndex ? targetIndex - 1 : targetIndex;
    col.widgets.splice(Math.max(0, Math.min(insertAt, col.widgets.length)), 0, widget);
  } else {
    for (const srcCol of columns.value) {
      const idx = srcCol.widgets.findIndex((w) => w.id === dragWidgetId.value);
      if (idx >= 0) {
        const [widget] = srcCol.widgets.splice(idx, 1);
        widget.columnId = columnId;
        if (isWidgetWidthSynced(widget)) {
          widget.colSpan = col.colSpan;
        } else {
          widget.colSpan = Math.min(widget.colSpan, col.colSpan);
        }
        col.widgets.splice(Math.min(targetIndex, col.widgets.length), 0, widget);
        break;
      }
    }
  }
}

function onEmptyColumnDragOver(e: DragEvent, columnId: number) {
  if (dragWidgetId.value === null) return;
  e.preventDefault();

  const col = columns.value.find((c) => c.id === columnId);
  if (!col) return;
  if (col.widgets.some((w) => w.id === dragWidgetId.value)) return;

  for (const srcCol of columns.value) {
    const idx = srcCol.widgets.findIndex((w) => w.id === dragWidgetId.value);
    if (idx >= 0) {
      const [widget] = srcCol.widgets.splice(idx, 1);
      widget.columnId = columnId;
      if (isWidgetWidthSynced(widget)) {
        widget.colSpan = col.colSpan;
      } else {
        widget.colSpan = Math.min(widget.colSpan, col.colSpan);
      }
      col.widgets.push(widget);
      break;
    }
  }
}

/** Commit the current (preview) array state to the API. */
async function commitWidgetDrop() {
  if (dragWidgetId.value === null) return;

  const widgetId = dragWidgetId.value;

  let targetColumnId: number | null = null;
  let finalIndex = -1;
  for (const col of columns.value) {
    const idx = col.widgets.findIndex((w) => w.id === widgetId);
    if (idx >= 0) {
      targetColumnId = col.id;
      finalIndex = idx;
      break;
    }
  }

  if (targetColumnId === null || finalIndex < 0) {
    restoreWidgetOrderSnapshot();
    clearWidgetDragState();
    return;
  }

  const movingAcrossColumns = dragFromColumnId.value !== null && dragFromColumnId.value !== targetColumnId;
  const sourceWidget = findWidget(widgetId);
  const sourceWasWidthSynced = sourceWidget ? isWidgetWidthSynced(sourceWidget) : false;
  const targetColumn = columns.value.find((c) => c.id === targetColumnId);

  if (!movingAcrossColumns && widgetOrderSnapshot.value) {
    const snapCol = widgetOrderSnapshot.value.find((snap) => snap.some((w) => w.id === widgetId));
    const originalIndex = snapCol?.findIndex((w) => w.id === widgetId) ?? -1;
    if (originalIndex === finalIndex) {
      clearWidgetDragState();
      return;
    }
  }

  clearWidgetDragState();

  try {
    await DashboardWidgetService.move(widgetId, { columnId: targetColumnId, position: finalIndex });
    if (movingAcrossColumns && sourceWasWidthSynced && targetColumn) {
      const movedWidget = findWidget(widgetId);
      if (movedWidget) {
        const syncedConfig = buildWidgetConfigWithWidthSync(movedWidget, true);
        pendingConfigs.value.delete(widgetId);
        movedWidget.colSpan = targetColumn.colSpan;
        movedWidget.config = syncedConfig;
        await DashboardWidgetService.update(widgetId, {
          colSpan: targetColumn.colSpan,
          config: syncedConfig,
        });
      }
    }
  } catch {
    await loadLayout();
  }
}

function onDragEnd() {
  if (dragWidgetId.value !== null) {
    restoreWidgetOrderSnapshot();
  }
  clearWidgetDragState();
}

function onAddColumnDragOver(e: DragEvent) {
  if (dragWidgetId.value === null || !canAddColumn.value) return;
  e.preventDefault();
  draggingOverAddColumn.value = true;
}

function onAddColumnDragLeave() {
  draggingOverAddColumn.value = false;
}

async function onDropOnAddColumn(e: DragEvent) {
  e.preventDefault();
  if (dragWidgetId.value === null || !canAddColumn.value) return;

  const widgetId = dragWidgetId.value;
  restoreWidgetOrderSnapshot();
  clearWidgetDragState();

  try {
    await DashboardWidgetService.createColumn(1);
    await loadLayout();
    const newColumn = columns.value[columns.value.length - 1];
    if (newColumn) {
      await DashboardWidgetService.move(widgetId, { columnId: newColumn.id, position: 0 });
      await loadLayout();
    }
  } catch {
    await loadLayout();
  }
}

function onConfigChange(payload: { id: number; config: Record<string, unknown> }) {
  const widget = findWidget(payload.id);
  const widthSynced = widget ? isWidgetWidthSynced(widget) : true;
  const nextConfig = withWidgetWidthSync(payload.config, widthSynced);
  pendingConfigs.value.set(payload.id, nextConfig);
}

async function onWidgetSpanChange(payload: { id: number; colSpan: number }) {
  const widget = findWidget(payload.id);
  if (!widget) return;

  const nextConfig = buildWidgetConfigWithWidthSync(widget, false);
  pendingConfigs.value.delete(widget.id);
  widget.colSpan = payload.colSpan;
  widget.config = nextConfig;
  try {
    await DashboardWidgetService.update(payload.id, { colSpan: payload.colSpan, config: nextConfig });
  } catch {
    await loadLayout();
  }
}

async function addColumn() {
  if (!canAddColumn.value) return;
  try {
    await DashboardWidgetService.createColumn(1);
    await loadLayout();
  } catch {
    // silently fail
  }
}

async function removeColumn(columnId: number) {
  try {
    await DashboardWidgetService.removeColumn(columnId);
    await loadLayout();
  } catch {
    // silently fail
  }
}

function maxSpanForColumn(column: DashboardColumn) {
  return 4;
}

function updateColumnSliderPosition() {
  const trigger = activeColumnSliderHostRef.value;
  if (!trigger) return;
  const rect = trigger.getBoundingClientRect();
  const panelWidth = 288; // keep in sync with overlay `w-72`
  const left = Math.min(Math.max(8, rect.left + (rect.width - panelWidth) / 2), Math.max(8, window.innerWidth - panelWidth - 8));
  const top = Math.max(8, rect.top - 6);
  activeColumnSliderStyle.value = {
    position: 'fixed',
    top: `${top}px`,
    left: `${left}px`,
  };
}

function toggleColumnSlider(columnId: number, e: MouseEvent) {
  if (activeColumnSliderId.value === columnId) {
    closeColumnSlider();
    return;
  }
  activeColumnSliderId.value = columnId;
  activeColumnSliderHostRef.value = e.currentTarget as HTMLElement | null;
  updateColumnSliderPosition();
}

function closeColumnSlider() {
  activeColumnSliderId.value = null;
  activeColumnSliderHostRef.value = null;
}

function onColumnSliderInput(column: DashboardColumn, e: Event) {
  const value = Number((e.target as HTMLInputElement).value);
  void updateColumnSpan(column, value);
}

function handleColumnSliderClickOutside(e: MouseEvent) {
  if (activeColumnSliderId.value === null) return;
  const target = e.target as Node | null;
  if (!target) return;
  if (activeColumnSliderHostRef.value?.contains(target)) return;
  if (activeColumnSliderPanelRef.value?.contains(target)) return;
  activeColumnSliderId.value = null;
}

const activeColumn = computed(() => {
  if (activeColumnSliderId.value === null) return null;
  return columns.value.find((column) => column.id === activeColumnSliderId.value) ?? null;
});

async function updateColumnSpan(column: DashboardColumn, nextSpan: number) {
  const maxSpan = maxSpanForColumn(column);
  const clamped = Math.max(1, Math.min(nextSpan, maxSpan));
  if (clamped === column.colSpan) return;

  const syncedWidgetUpdates: Array<{ id: number; config: string; colSpan: number }> = [];
  column.colSpan = clamped;
  column.widgets = column.widgets.map((widget) => {
    const widthSynced = isWidgetWidthSynced(widget);
    if (widthSynced) {
      const nextConfig = buildWidgetConfigWithWidthSync(widget, true);
      syncedWidgetUpdates.push({ id: widget.id, config: nextConfig, colSpan: clamped });
      return {
        ...widget,
        colSpan: clamped,
        config: nextConfig,
      };
    }
    return {
      ...widget,
      colSpan: Math.min(widget.colSpan, clamped),
    };
  });

  try {
    await DashboardWidgetService.updateColumn(column.id, clamped);
    if (syncedWidgetUpdates.length > 0) {
      await Promise.all(
        syncedWidgetUpdates.map((update) =>
          DashboardWidgetService.update(update.id, {
            colSpan: update.colSpan,
            config: update.config,
          }),
        ),
      );
    }
  } catch {
    await loadLayout();
  }
}

async function onWidgetSyncWidth(widgetId: number) {
  const widget = findWidget(widgetId);
  if (!widget) return;
  const column = columns.value.find((candidate) => candidate.id === widget.columnId);
  if (!column) return;

  const nextConfig = buildWidgetConfigWithWidthSync(widget, true);
  pendingConfigs.value.delete(widget.id);
  widget.colSpan = column.colSpan;
  widget.config = nextConfig;
  try {
    await DashboardWidgetService.update(widget.id, { colSpan: column.colSpan, config: nextConfig });
  } catch {
    await loadLayout();
  }
}

function columnSpanClass(column: DashboardColumn, columnIndex: number) {
  const classNames = [columnSpanClassMap[column.colSpan] ?? 'lg:col-span-1'];
  const startClass = lgColumnStartClassMap[lgRowStartsByIndex.value.get(columnIndex) ?? 1];
  if (startClass) classNames.push(startClass);
  return classNames;
}

function widgetGridClass(column: DashboardColumn) {
  return internalColumnGridClassMap[column.colSpan] ?? 'md:grid-cols-1';
}

function findWidget(widgetId: number): DashboardWidgetType | undefined {
  for (const column of columns.value) {
    const found = column.widgets.find((widget) => widget.id === widgetId);
    if (found) return found;
  }
  return undefined;
}

async function finishEditing() {
  editMode.value = false;
  closeColumnSlider();
  if (pendingConfigs.value.size > 0) {
    const updates = Array.from(pendingConfigs.value.entries()).map(([id, config]) =>
      DashboardWidgetService.update(id, { config: JSON.stringify(config) }),
    );
    pendingConfigs.value.clear();
    try {
      await Promise.all(updates);
      await loadLayout();
    } catch {
      // silently fail
    }
  }
}

async function applyTemplate(templateId: number) {
  showBrowseTemplates.value = false;
  if (totalWidgets.value > 0) {
    const confirmed = await confirmModalRef.value?.showConfirm(
      'This will replace your current dashboard with the selected template. Would you like to continue?',
    );
    if (!confirmed) return;
  }
  templatesAutoOpened.value = false;
  try {
    await DashboardTemplateService.apply(templateId);
    editMode.value = false;
    await loadLayout();
  } catch {
    console.error('Failed to apply template to user dashboard');
  }
}

function closeBrowseTemplates() {
  showBrowseTemplates.value = false;
  templatesAutoOpened.value = false;
}

async function maybeAutoOpenTemplates() {
  if (totalWidgets.value !== 0) return;
  try {
    const templates = await DashboardTemplateService.list();
    if (templates.length === 0) return;
    templatesAutoOpened.value = true;
    showBrowseTemplates.value = true;
  } catch {
    // silently fall back to existing empty state
  }
}

function onTemplateCreated() {
  showCreateTemplate.value = false;
}

onMounted(async () => {
  document.addEventListener('mousedown', handleColumnSliderClickOutside);
  window.addEventListener('resize', updateColumnSliderPosition);
  window.addEventListener('scroll', updateColumnSliderPosition, true);
  await loadLayout();
  await maybeAutoOpenTemplates();
});

onBeforeUnmount(() => {
  document.removeEventListener('mousedown', handleColumnSliderClickOutside);
  window.removeEventListener('resize', updateColumnSliderPosition);
  window.removeEventListener('scroll', updateColumnSliderPosition, true);
});
</script>

<template>
  <div class="relative mx-auto px-2 py-1 pt-4 sm:px-3 lg:px-12 xl:px-36">
    <div class="absolute top-4 right-4 flex flex-wrap items-center justify-end gap-2">
      <AppButton
        v-if="!isCurrentHomepage && !editMode"
        :loading="settingHomepage"
        @click="setAsHomepage">
        Set as Homepage
      </AppButton>
      <AppButton
        v-if="editMode && isAdmin"
        @click="showCreateTemplate = true">
        Create Template
      </AppButton>
      <AppButton
        v-if="editMode || totalWidgets === 0"
        @click="showBrowseTemplates = true">
        Browse Templates
      </AppButton>
      <AppButton
        :primary="editMode"
        @click="editMode ? finishEditing() : (editMode = true)">
        {{ editMode ? 'Done' : 'Edit' }}
      </AppButton>
    </div>

    <div
      class="header mb-4"
      :class="editMode ? 'mt-8' : ''">
      <UniversalSearch />
    </div>

    <!-- Loading -->
    <div
      v-if="loading"
      class="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-8">
      <div class="col-span-1 h-48 skeleton md:col-span-2 lg:col-span-8"></div>
    </div>

    <!-- Empty state -->
    <div
      v-else-if="totalWidgets === 0 && !editMode"
      class="flex flex-col items-center gap-3 py-16 text-base-content/50">
      <p>Your dashboard is empty.</p>
      <button
        class="btn text-white btn-sm btn-primary"
        @click="enterEditModeAndAddFirstWidget">
        Add your first widget
      </button>
    </div>

    <!-- Column layout -->
    <div
      v-else
      class="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-8"
      @dragover="onColumnContainerDragOver"
      @drop.prevent="commitColumnDrop">
      <div
        v-for="(column, columnIndex) in columns"
        :key="column.id"
        :class="[
          columnSpanClass(column, columnIndex),
          editMode ? 'rounded-lg border bg-base-100 p-3' : 'p-0',
          dragColumnId === column.id ? 'opacity-60 ring-2 ring-primary/40' : '',
        ]"
        class="col-span-1 transition-all duration-200">
        <div
          v-if="editMode"
          class="mb-3 flex items-center justify-between">
          <div class="flex items-center gap-2">
            <div
              class="drag-handle flex cursor-grab text-base-content/70 hover:text-base-content active:cursor-grabbing"
              draggable="true"
              @mousedown.stop
              @dragstart="onColumnDragStart($event, column.id)"
              @dragend="onColumnDragEnd">
              <Grip :size="16" />
            </div>
            <div class="text-sm font-semibold">Column {{ column.position + 1 }}</div>
          </div>
          <div class="flex items-center gap-2">
            <div class="relative">
              <button
                class="btn gap-1 px-2 btn-ghost btn-xs"
                @click="toggleColumnSlider(column.id, $event)">
                <SlidersHorizontal :size="12" />
                <span class="font-semibold">{{ column.colSpan }}</span>
              </button>
            </div>
            <button
              class="btn btn-xs"
              :disabled="columns.length <= 1"
              @click="removeColumn(column.id)">
              <Trash2 :size="16" />
            </button>
          </div>
        </div>

        <div
          class="grid grid-cols-1 gap-4"
          :class="widgetGridClass(column)"
          @dragover.prevent="onWidgetContainerDragOver($event, column.id)"
          @drop.prevent="commitWidgetDrop">
          <template
            v-for="widget in column.widgets"
            :key="widget.id">
            <DashboardWidget
              :widget="widget"
              :max-span="column.colSpan"
              :width-synced="isWidgetWidthSynced(widget)"
              :edit-mode="editMode"
              :dragging="dragWidgetId === widget.id"
              :draggable="editMode"
              @remove="removeWidget"
              @config-change="onConfigChange"
              @span-change="onWidgetSpanChange"
              @sync-width="onWidgetSyncWidth"
              @dragstart="onDragStart($event, widget.id, column.id)"
              @dragend="onDragEnd" />
          </template>
        </div>

        <div
          v-if="editMode && column.widgets.length === 0"
          class="mt-2 rounded border border-dashed p-4 text-center text-xs text-base-content/50"
          @dragover.prevent="onEmptyColumnDragOver($event, column.id)"
          @drop.prevent="commitWidgetDrop">
          Drag widgets here
        </div>

        <button
          v-if="editMode"
          type="button"
          class="btn mt-2 h-8 min-h-8 w-full border border-dashed border-base-content/20 font-normal text-base-content/70 btn-ghost btn-xs hover:border-primary/35 hover:bg-base-200/50"
          @click="openAddWidgetModal(column.id)">
          <span>Add widget</span>
        </button>
      </div>

      <div
        v-if="editMode"
        :class="addColumnSlotClass"
        @dragover="onAddColumnDragOver"
        @dragleave="onAddColumnDragLeave"
        @drop="onDropOnAddColumn">
        <button
          type="button"
          class="btn flex aspect-square h-18 w-18 shrink-0 flex-col items-center justify-center gap-1 border border-dashed p-2 font-normal btn-ghost transition-all duration-200 btn-xs sm:h-20 sm:w-20"
          :class="
            draggingOverAddColumn
              ? 'scale-105 border-primary bg-primary/10 text-primary ring-2 ring-primary/30'
              : 'border-base-content/20 text-base-content/70 hover:border-primary/35 hover:bg-base-200/50'
          "
          :disabled="!canAddColumn"
          @click="addColumn">
          <span class="text-xs leading-none">{{ draggingOverAddColumn ? 'Drop' : 'Add' }}</span>
          <span class="text-xs leading-none">{{ draggingOverAddColumn ? 'here' : 'Column' }}</span>
        </button>
      </div>
    </div>

    <AddWidgetModal
      :show="showAddModal"
      @close="showAddModal = false"
      @add="addWidget" />

    <BrowseTemplatesModal
      :show="showBrowseTemplates"
      :is-admin="isAdmin"
      :auto-opened="templatesAutoOpened"
      @close="closeBrowseTemplates"
      @apply="applyTemplate" />

    <CreateTemplateModal
      :show="showCreateTemplate"
      @close="showCreateTemplate = false"
      @created="onTemplateCreated" />

    <ConfirmModal ref="confirmModalRef" />
  </div>
  <Teleport to="body">
    <div
      v-if="activeColumn"
      ref="activeColumnSliderPanelRef"
      class="z-40 w-72 rounded-xl border bg-base-100 p-4 shadow-lg"
      :style="activeColumnSliderStyle"
      @mouseleave="closeColumnSlider">
      <div class="mb-3 flex items-center justify-between text-sm text-base-content/70">
        <span>Column width</span>
        <span class="font-semibold text-base-content">{{ activeColumn.colSpan }}</span>
      </div>
      <input
        class="range w-full range-primary range-sm"
        type="range"
        min="1"
        :max="maxSpanForColumn(activeColumn)"
        step="1"
        :value="activeColumn.colSpan"
        @input="onColumnSliderInput(activeColumn, $event)" />
      <div class="mt-2 flex justify-between text-xs text-base-content/60">
        <span
          v-for="option in maxSpanForColumn(activeColumn)"
          :key="option"
          >{{ option }}</span
        >
      </div>
    </div>
  </Teleport>
</template>
